import {DeleteOutlined, EditOutlined, MoreOutlined, QrcodeOutlined} from "@ant-design/icons";
import {Button, Dropdown, Divider, Modal, notification, Upload} from "antd";
import React, {useState} from "react";
import {PartnerCreateDTO} from "../../../types/partner/PartnerCreateDTO";
import {
  useDeletePartnerMutation,
  useGetPartnerQrCodeMutation,
  useUpdatePartnerMutation,
} from "../../../store/api/PartnerApi";
import { PartnerDTO } from "../../../types/partner/PartnerDTO";
import PartnerCreateForm from "../create/PartnerCreateForm";
import { useGetSelfEmployeeQuery } from "../../../store/api/EmployeeApi";

interface PartnerActionButtonProps {
  partner: PartnerDTO;
}

const PartnerActionButton: React.FC<PartnerActionButtonProps> = ({
  partner,
}) => {
  const [visibleUpdate, setVisibleUpdate] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [visibleQrCode, setVisibleQrCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [updatePartner] = useUpdatePartnerMutation();
  const [deletePartner] = useDeletePartnerMutation();
  const [getQrCodePartner] = useGetPartnerQrCodeMutation();
  const { data: employee } = useGetSelfEmployeeQuery();
  const permission = employee?.data?.permission;

  const items = [
    {
      key: "qrCode",
      label: (
        <>
          <QrcodeOutlined /> Получить QR код
        </>
      ),
    },
    ...(permission?.canUpdatePartners
      ? [
          {
            key: "update",
            label: (
              <>
                <EditOutlined /> Изменить
              </>
            ),
          },
        ]
      : []),
    ...(permission?.canDeletePartners
      ? [
          {
            key: "delete",
            label: (
              <>
                <DeleteOutlined /> Удалить
              </>
            ),
          },
        ]
      : []),
  ];

  const onClickHandler = async (id: string, key: string) => {
    switch (key) {
      case "update": {
        setVisibleUpdate(true);
        break;
      }
      case "delete": {
        setVisibleDelete(true);
        break;
      }
      case "qrCode": {
        await getQrCodePartner(id)
          .unwrap()
          .then((res) => setQrCodeUrl(res))
          .catch((error) =>
            notification.error({
              message: `Ошибка ${error.status}`,
              description: error.data.message,
              duration: 5,
            })
          );
        setVisibleQrCode(true);
        break;
      }
    }
  };

  const onCloseUpdateHandler = () => setVisibleUpdate(false);

  const onFinishUpdateHandler = async (id: string, dto: PartnerCreateDTO) => {
    setLoadingUpdate(true);

    const omitNullish = (obj: Record<string, any>): Record<string, any> =>
      Object.entries(obj).reduce<Record<string, any>>((acc, [key, value]) => {
        if (value === null || value === undefined) return acc;
        if (typeof value === "object" && !Array.isArray(value)) {
          const nested = omitNullish(value);
          if (Object.keys(nested).length > 0) acc[key] = nested;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});

    const address = omitNullish({
      ...dto.address,
      branch: undefined,
      branch_id: dto.address.branch?.id,
    });

    const modifiedData = omitNullish({ ...dto, address });

    await updatePartner({ id, dto: modifiedData })
      .then(() =>
        notification.success({
          message: "Заведение успешно изменено!",
          duration: 5,
        })
      )
      .catch((error) =>
        notification.error({
          message: `Ошибка ${error.status}`,
          description: error.data.message,
          duration: 5,
        })
      );
    setVisibleUpdate(false);
    setLoadingUpdate(false);
  };

  const onCloseDeleteHandler = () => setVisibleDelete(false);

  const onFinishDeleteHandler = async (id: string) => {
    setLoadingDelete(true);
    await deletePartner(id)
      .then(() =>
        notification.success({
          message: "Заведение успешно удалено!",
          duration: 5,
        })
      )
      .catch((error) =>
        notification.error({
          message: `Ошибка ${error.status}`,
          description: error.data.message,
          duration: 5,
        })
      );
    setVisibleDelete(false);
    setLoadingDelete(false);
  };

  const onCloseQrCodeHandler = () => setVisibleQrCode(false);

  return (
    <>
      <Dropdown
        menu={{
          items,
          onClick: (item) => onClickHandler(partner.id, item.key),
        }}
      >
        <Button>
          <MoreOutlined />
        </Button>
      </Dropdown>

      <Modal
        title="Изменить заведение"
        open={visibleUpdate}
        onCancel={onCloseUpdateHandler}
        okText="Изменить"
        destroyOnClose
        okButtonProps={{
          disabled: loadingUpdate,
          form: "partner-create-form",
          htmlType: "submit",
        }}
      >
        <Divider />
        <PartnerCreateForm
          partner={partner}
          onFinish={(data) => onFinishUpdateHandler(partner.id, data)}
        />
      </Modal>

      <Modal
        title="Вы действительно хотите удалить?"
        open={visibleDelete}
        onCancel={onCloseDeleteHandler}
        okText="Удалить"
        destroyOnClose
        onOk={() => onFinishDeleteHandler(partner.id)}
        okButtonProps={{
          disabled: loadingDelete,
        }}
      />

      <Modal
        open={visibleQrCode}
        onCancel={onCloseQrCodeHandler}
        footer={null}
        centered
        mask={true}
        width={460}
      >
        <Upload
          showUploadList={{
            showDownloadIcon: true,
            showPreviewIcon: false,
            showRemoveIcon: false,
          }}
          fileList={[
            {
              url: qrCodeUrl,
              uid: "",
              name: qrCodeUrl,
              status: "done",
            },
          ]}
          listType="picture-card"
        ></Upload>
      </Modal>
    </>
  );
};

export default PartnerActionButton;
